<template>
  <div id="puzzle-content">
    <div
      id="context"
    >During the hunt, teams unlocked the meta after answering all five regular puzzles.</div>
    <div>
      <p>
        Welcome to the
        <span class="smallcaps">Society of the Undistinguished</span>.
        The members of the
        <span class="smallcaps">Society</span> are having trouble with the
        <span class="smallcaps">
          Enigmeta
          Machine
        </span>, and need your help. They know you're tired of instruction-less puzzles, and have provided
        detailed instructions for you to follow to solve this final puzzle...
      </p>
    </div>
    <div>
      <img src="@/assets/img/meta-society.jpg" />
    </div>
    <div id="meta-1-checkers">
      <GuessChecker
        v-for="(member, name) in meta1"
        :key="name"
        :simple="member.placeholder"
        :answer="member.hash"
      ></GuessChecker>
    </div>

    <div id="meta-1">
      <p>
        Each member of the
        <span class="smallcaps">Society of the Undistinguished</span> has a favourite word, the
        answer to a puzzle you solved earlier today. Enter each member's favourite word below their picture and
        confirm them by pressing the check button.
      </p>
    </div>

    <div
      id="context"
    >The next section was revealed after matching each member to their favourite word.</div>

    <div id="meta-2">
      <p>
        Excellent work. Now it's time to pair up and follow all the steps in the
        <span class="smallcaps">Enigmeta Machine</span>'s
        arcane algorithm. At the beginning of each pair, each teammate, acting as a member in the pair, starts with their
        favourite word. The members of each pair perform their steps in order, simultaneously in sync with their partner.
      </p>
      <p>
        (For instance, in the
        <span class="explosive">Explosive Roleplayers</span>, Wegener (teammate 1) and Galilei
        (teammate 2) perform their own Step 1 starting with their favourite word at the same time, and then perform their
        Step 2 on the result at the same time, and so on.) At the end of each pair's algorithm, you will end up with that
        pair's <span class="enigmatic">enigmatic word</span>. Enter it into the textbox below each pair and confirm them.
      </p>

      <div class="meta-pair">
        <div class="meta-pair-name"><span class="explosive">The Explosive Roleplayers</span>: Wegener and Galilei</div>
        <div class="meta-algo">
          <div class="meta-left">
            Wegener's instructions:
            <ol>
              <li>Remove the first N and replace with the letters ILY.</li>
              <li>Remove the six letters in LONELY and rearrange the remaining letters to find a famous ship.</li>
              <li>
                The first two letters sink into the ocean. Remove them. Grab your partner's hand and promise
                you'll never let go.
              </li>
            </ol>
          </div>
          <div class="meta-right">
            Galilei's instructions:
            <ol>
              <li>Replace V with BOMB.</li>
              <li>Drop out of college and delete UNI. Replace ER with DISP.</li>
              <li>
                Keep only the leftmost two and rightmost two letters. Grab your partner's hand and ask him to
                never let go.
              </li>
            </ol>
          </div>
        </div>Galilei's letters eat Wegener's letters to find the pair's <span class="enigmatic">enigmatic word</span>.
        <br />(Put Wegener's letters in the middle, and Galilei's letters around the outside.)
        <!-- SUBMIT -->

        <GuessChecker
          :key="'meta-2-explosive'"
          :simple="meta2['meta-2-explosive'].placeholder"
          :answer="meta2['meta-2-explosive'].hash"
        ></GuessChecker>
      </div>
      <div class="meta-pair">
        <div class="meta-pair-name"><span class="free">The Free Marketeers</span>: Galilei and Maier</div>
        <div class="meta-algo">
          <div class="meta-left">
            Galilei's instructions:
            <ol>
              <li>
                Replace a
                <i>two letter</i> roman numeral < 26 with the letter for that number (A=1, B=2, ...)
              </li>
              <li>
                Remove R and trade it for a different letter with Maier. High five your partner. Put her letter at the end of your
                word.
              </li>
              <li>No lan parties. Remove LAN.</li>
              <li>
                Rearrange the letters to rhyme with the surname of protagonist Robinson (in the distinguished
                novel by Daniel Defoe).
              </li>
            </ol>
          </div>
          <div class="meta-right">
            Maier's instructions:
            <ol>
              <li>Add a single letter to make a male Spanish cat inside your word.</li>
              <li>
                Remove the letter you just added and trade it for a different letter with Galilei. High five your partner. Insert his
                letter in both the front and the end of your word.
              </li>
              <li>Remove the two letters in the abbreviation for the Silver State.</li>
              <li>
                Keep only the consonants. Do the consonant dance (get up and spin around before sitting back
                down)!
              </li>
            </ol>
          </div>
        </div>Riffle shuffle the pair's letters to find their <span class="enigmatic">enigmatic word</span>.
        <br />(Intersperse the letters from each partner, one letter at a time, the
        one with more letters going first.)
        <!-- SUBMIT -->
        <GuessChecker
          :key="'meta-2-free'"
          :simple="meta2['meta-2-free'].placeholder"
          :answer="meta2['meta-2-free'].hash"
        ></GuessChecker>
      </div>
      <div class="meta-pair">
        <div class="meta-pair-name"><span class="wordplay">The Wordplay Duo</span>: Maier and van Gogh</div>
        <div class="meta-algo">
          <div class="meta-left">
            Maier's instructions:
            <ol>
              <li>
                Take the 3rd, 4th, 5th, and 8th letter of your word to spell a
                <i>hinged portal</i> or something
                <i>logical</i>.
              </li>
              <li>
                Word ladder time! Change one letter to make
                <i>destiny</i>, or a
                <i>force outside our control</i>.
              </li>
              <li>
                Change one letter to make a
                <i>French party</i>.
              </li>
              <li>Replace the first letter with R. Yell "ARRRRR!!" like a pirate as loud as you can.</li>
            </ol>
          </div>
          <div class="meta-right">
            van Gogh's instructions:
            <ol>
              <li>
                Complete this analogy with your word: HEAR is to YOUR WORD, as SPEAK is to ???? (4).
                Use this answer to continue.
              </li>
              <li>
                Word ladder time! Change one letter to make a
                <i>tiny parasite</i>.
              </li>
              <li>
                Change one letter to make a
                <i>deep bog</i> (metaphorically speaking, a
                <i>predicament</i> or
                <i>quandary</i>).
              </li>
              <li>Add the letter P to the end. Yell "NO PIRATES ALLOWED!!!" like an English person.</li>
            </ol>
          </div>
        </div>Append the letters from each partner together (Maier first, then van Gogh) and reverse to find the pair's
        <span class="enigmatic">enigmatic word</span>.
        <!-- SUBMIT -->
        <GuessChecker
          :key="'meta-2-wordplay'"
          :simple="meta2['meta-2-wordplay'].placeholder"
          :answer="meta2['meta-2-wordplay'].hash"
        ></GuessChecker>
      </div>
      <div class="meta-pair">
        <div class="meta-pair-name"><span class="lorem">The L0r3m 1p5um Tryhards</span>: van Gogh and Wegener</div>
        <div class="meta-algo">
          <div class="meta-left">
            van Gogh's instructions:
            <ol>
              <li>
                Add a single hexadecimal digit in the middle of your word to spell out van Gogh's favourite type
                of coffee.
              </li>
              <li>
                Taking what you have as a hexadecimal number, numerically add 0xD0DE2F to find another English
                word.
              </li>
              <li>Take a nap. Snore extremely loudly.</li>
              <li>Your partner wakes you up. Let out a huge yawn.</li>
              <li>Remove the last letter.</li>
            </ol>
          </div>
          <div class="meta-right">
            Wegener's instructions:
            <ol>
              <li>Replace the chemical element name with its full Latin name.</li>
              <li>Long live the Latin Empire! Add seven letters in the middle to find its capital city inside your word.</li>
              <li>
                Remove the longest contiguous word that isn't a proper noun starting from the first letter.
                Remove the longest contiguous word ending at the last letter.
              </li>
              <li>
                Oi, are you doing all the work while your partner slacks off? Wake up your partner, then shift
                your letters two to the right, wrapping around.
                <br />(For example, shifting the letters ABCDE two to
                the right would make DEABC.)
              </li>
              <li>The Iberian peninsula is ours! Remove the four letters of a Spanish boy from what you have.</li>
            </ol>
          </div>
        </div>
        Append the letters from each partner together (van Gogh first, then Wegener). That's Greek to me! Divide by two numerically (using Greek prefixes) and convert it to Latin to find the pair's <span class="enigmatic">enigmatic word</span>.
        <GuessChecker
          :key="'meta-2-lorem'"
          :simple="meta2['meta-2-lorem'].placeholder"
          :answer="meta2['meta-2-lorem'].hash"
        ></GuessChecker>
      </div>
    </div>

    <p></p> 

    <div id="context">The final section was revealed after finding all four enigmatic words.</div>

    <div id="meta-3">
      <p>
        Incredible, you found all the <span class="enigmatic">enigmatic words</span>&thinsp;!
        <br />This is it, the final stretch. Kafka, the
        <span class="smallcaps">
          Master of Metamorphosis
        </span>, will show you the way. Kafka's instructions:
      </p>
      <ol>
        <li>Take Kafka's favourite word and convert it to six numbers (A=1, B=2, ...)</li>
        <li>
          Take the absolute difference between each consecutive pair to get five new numbers. (e.g. 10 1 2 3 5 8 -> 9
          1 1 2 3)
        </li>
        <li>
          One of these numbers is two digits long. Take the tens-place digit of that number off, and add that digit
          to the number after it. (e.g. 3 14 5 -> 3 4 6)
        </li>
        <li>Swap positions of the last two numbers.</li>
        <li>Replace the number 0 with an integer to get a strictly ascending sequence (a < b < c < ...).</li>
      </ol>These are your metamorphosis numbers.
      Index these numbers to get four letters from each enigmatic word:
      <ul>
        <li>
          Skipping the
          <i>second</i> number into the
          <span class="explosive">Explosive Roleplayers</span>' enigmatic
          word.
        </li>
        <li>
          Skipping the
          <i>third</i> number into the
          <span class="free">Free Marketeers</span>' enigmatic word.
        </li>
        <li>
          Skipping the
          <i>fifth</i> number into the
          <span class="wordplay">Wordplay Duo</span>'s enigmatic word.
        </li>
        <li>
          Skipping the
          <i>third</i> number into the
          <span class="lorem">L0r3m 1p5um Tryhards</span>' enigmatic
          word.
        </li>
      </ul>Take your 4x4 letter matrix and transpose it to find out what's wrong with the
      <span class="smallcaps">Enigmeta Machine</span>!
      Submit this at the very top as your final answer.
    </div>
  </div>
</template>

<script>
import GuessChecker from "@/components/GuessChecker.vue";

// pass in prop for simplified feedback from guesschecker
export default {
  components: {
    GuessChecker
  },
  computed: {
    meta1() {
      return this.$store.state.meta["meta-1"];
    },
    meta2() {
      return this.$store.state.meta["meta-2"];
    }
  }
};
</script>

<style lang="scss" scoped>
#puzzle-content {
  #context {
    text-align: center;
  }

  text-align: left;
}

img {
  padding-top: 16px;
}
span.smallcaps {
  font-weight: bold;
  font-variant: small-caps;
  text-shadow: 1px 1px 1px rgb(196, 153, 10);
}
span.enigmatic {
  // font-weight: bold;
  font-style: italic;
  text-shadow: 1px 1px 1px rgb(196, 153, 10);
//   color: #d9d9d9;
// background: #e8e8e8;
text-shadow: -1px -1px 1px rgba(255,255,255,.1), 1px 1px 1px rgba(0,0,0,.5);
}
span.explosive {
  font-weight: bold;
  font-variant: small-caps;
  text-shadow: 1px 1px 1px rgb(10, 177, 214);
}
span.free {
  font-weight: bold;
  font-variant: small-caps;
  text-shadow: 1px 1px 1px rgb(230, 104, 54);
}
span.wordplay {
  font-weight: bold;
  font-variant: small-caps;
  text-shadow: 1px 1px 1px rgb(204, 44, 202);
}
span.lorem {
  font-weight: bold;
  font-variant: small-caps;
  text-shadow: 1px 1px 1px rgb(27, 199, 76);
}

#meta-1-checkers {
  display: flex;
  justify-content: space-around;
  flex-wrap:wrap;
  // flex-basis: 100px;

  // flex-basis: 20%;
  >>> #guess-checker {
    max-width: 20%;
    min-width: 150px;
  }
  >>> #guess-feedback {
    font-size: 14px;
  }
  // padding-bottom: 30px;
}

.meta-pair-name {
  padding: 20px 0 20px 0;
  font-size: 40px;
  // font-variant: small-caps;
}

.meta-algo {
  display: flex;
  justify-content: space-around;
}

.meta-left,
.meta-right {
  max-width: 50%;
}
</style>